import type { IRogue, Product } from '@/js/services/RogueTypes'

export type RuleResult = boolean | null

export interface SharedRuleContext {
  rogue: IRogue
  rack: Product
  product: Product
  rackBeamColor: unknown
  sanitizedHardwareName: string
}

// Always show satin black top crossmember
export function ruleSatinBlackTopCrossmember({ product }: SharedRuleContext): RuleResult {
  const isSatinBlackTopCrossmember = product.name.includes('Top Crossmember') && product.name.includes('Satin Black')
  
  return isSatinBlackTopCrossmember ? true : null
}

// Hide offset pullup bar on non-80" racks
export function ruleOffsetPullupBar({ rack, product }: SharedRuleContext): RuleResult {
  if (!rack.attributes.rack_height?.toString()?.includes('80') && product.name.includes('Offset')) {
    return false
  }
  
  return null
}

// Hide front pullup bars incompatible with 80" racks
export function ruleNonCompatible80PullupBars({ rack, product }: SharedRuleContext, bundleOptionTitle: string): RuleResult {
  const fatSkinnyBar = product.name.includes('Fat') && product.name.includes('Skinny')
  const nonCompatible80PullupBars =
    (product.name.includes('Bar') && !product.attributes.rack_height_restriction?.toString().includes('80"')) ||
    (fatSkinnyBar && !product.attributes.rack_height_restriction?.toString().includes('80"'))

  if (rack.attributes.rack_height?.toString()?.includes('80') && nonCompatible80PullupBars && !bundleOptionTitle.includes('Lat Bar')) {
    return false
  }
  
  return null
}

// Hide all rear crossmembers except the top crossmember on 80" racks
export function ruleRearCrossmembersOn80({ rack, product }: SharedRuleContext): RuleResult {
  const topCrossmember = product.name.includes('Top') && product.attributes.rack_height_restriction
  if (rack.attributes.rack_height?.toString()?.includes('80') && product.name.includes('Nameplate') && !topCrossmember) {
    return false
  }
  
  return null
}

// Always show black crown with matching hardware, hide colored crown with mismatched hardware
export function ruleCrownHardware({ product, rackBeamColor, sanitizedHardwareName }: SharedRuleContext): RuleResult {
  const sanitizedCrownName = product.name.includes('Crown') ? product.name?.replace(/^.*\//, '') : product.name
  const hasMatchingCrownHardware = sanitizedCrownName?.includes(sanitizedHardwareName)
  const hasMatchingCrownColor = product.name.includes('Crown') && product.attributes.beam_color === rackBeamColor
  const isBlackCrown = product.name.includes('Crown') && product.name.includes('Satin Black')

  if (isBlackCrown && hasMatchingCrownHardware) return true
  if (!hasMatchingCrownHardware && hasMatchingCrownColor) return false
  
  return null
}

// Hide mismatched beam colors (nameplates/beams must match rack color)
export function ruleBeamColor({ product, rackBeamColor }: SharedRuleContext): RuleResult {
  if (
    rackBeamColor &&
    !product.name.includes('Nameplate') &&
    product.attributes.beam_color &&
    product.attributes.beam_color !== rackBeamColor
  ) {
    return false
  }
  
  return null
}

// Hide duplicate textured black nameplate options
export function ruleTexturedBlackNameplate({ rack, product }: SharedRuleContext): RuleResult {
  const rackSupportsTexturedBlackNameplate =
    rack.name?.includes('RML-490') || rack.name?.includes('RML-690') || rack.name?.includes('FML-6')
  const texturedBlackRackColorSelected = rack.name?.includes('Textured Black') ?? false
  const isNameplateProduct = product.name.includes('Nameplate')
  const texturedBlackProductSelected = product.name.includes('Textured Black')

  if (rackSupportsTexturedBlackNameplate && isNameplateProduct && texturedBlackRackColorSelected !== texturedBlackProductSelected) {
    return false
  }
  
  return null
}

/**
 * Run all shared rules in order.
 * Returns the first definitive true/false, or null if no rule has an opinion.
 */
export function applySharedRules(context: SharedRuleContext, bundleOptionTitle: string): RuleResult {
  const rules: RuleResult[] = [
    ruleSatinBlackTopCrossmember(context),
    ruleOffsetPullupBar(context),
    ruleNonCompatible80PullupBars(context, bundleOptionTitle),
    ruleRearCrossmembersOn80(context),
    ruleCrownHardware(context),
    ruleTexturedBlackNameplate(context),
    ruleBeamColor(context), // Beam color last — it's a broad catch-all
  ]

  for (const result of rules) {
    if (result !== null) return result
  }
  
  return null
}